import { Application, ApplicationData } from "./generator/applicationModels";
import {
  EvacuationLocation,
  FamilyMember,
  FamilyMembers,
  FirstNationStatus,
  PaymentInformation,
  PersonalInformation,
  StepData,
  TemporaryContactInfo,
  TermsOfUse,
} from "./generator/sectionModels";
import {
  maleFirstNames,
  femaleFirstNames,
  lastNames,
  genders,
  albertaPostalCodePrefixes,
  streetNames,
  albertaCities,
  countries,
  provinces,
} from "./generator/data";

export function randomInt(max: number): number {
  const array = new Uint32Array(1);
  window.crypto.getRandomValues(array);
  return array[0] % max;
}

export function generateMobile(): string {
  const areacode = [`${randomInt(10)}`, `${randomInt(10)}`, `${randomInt(10)}`];
  const firstNumbers = [
    `${randomInt(10)}`,
    `${randomInt(10)}`,
    `${randomInt(10)}`,
  ];
  const lastNumbers = [
    `${randomInt(10)}`,
    `${randomInt(10)}`,
    `${randomInt(10)}`,
    `${randomInt(10)}`,
  ];

  const phoneNumber: string = `(${areacode.join("")}) ${firstNumbers.join(
    ""
  )}-${lastNumbers.join("")}`;

  return phoneNumber;
}

export function generatePersonalDetails() {
  const gender: string = genders[randomInt(3)];
  let name: string = "";

  if (gender == "M") {
    name = maleFirstNames[randomInt(maleFirstNames.length)];
  } else {
    name = femaleFirstNames[randomInt(femaleFirstNames.length)];
  }

  const firstName: string = name;
  name = "";

  const lastName: string = lastNames[randomInt(lastNames.length)];
  const hasMiddleName: boolean = randomInt(2) > 0;

  if (hasMiddleName) {
    name =
      gender == "M"
        ? maleFirstNames[randomInt(maleFirstNames.length)]
        : femaleFirstNames[randomInt(femaleFirstNames.length)];
  }

  const middleName: string = name;

  const fullName: string = hasMiddleName
    ? `${firstName} ${middleName} ${lastName}`
    : `${firstName} ${lastName}`;

  const address = generateAddress();

  const personalDetails = {
    firstName,
    middleName,
    lastName,
    fullName,
    gender,
    dob: generateDOB(),
    ...address,
    mobile: generateMobile(),
    phone: generateMobile(),
  };

  return personalDetails;
}

export function generatePostalCode() {
  const allowedLetters = [
    "A",
    "B",
    "C",
    "E",
    "G",
    "H",
    "J",
    "K",
    "L",
    "M",
    "N",
    "P",
    "R",
    "S",
    "T",
    "V",
    "X",
    "Y",
  ];

  //Hardcoded for alberta
  const prefix: string =
    albertaPostalCodePrefixes[randomInt(albertaPostalCodePrefixes.length)];
  const postfix: string = `${randomInt(10)}${
    allowedLetters[randomInt(allowedLetters.length)]
  }${randomInt(10)}`;

  const postalCode: string = `${prefix} ${postfix}`;

  return postalCode;
}

export function generateStreetAddressLine1() {
  const houseNumber: number = randomInt(1000);
  const streetAddressLine1: string = `${houseNumber} ${
    streetNames[randomInt(streetNames.length)]
  }`;

  return streetAddressLine1;
}

export function generateAddress() {
  const locality: string = albertaCities[randomInt(albertaCities.length)];
  const streetAddressLine1: string = generateStreetAddressLine1();
  const streetAddressLine2: string = "";
  const postalCode: string = generatePostalCode();
  const country: string = countries[randomInt(countries.length)];
  const province: string = `${country}-${
    provinces[randomInt(provinces.length)]
  }`;

  const address = {
    locality,
    streetAddressLine1,
    streetAddressLine2,
    postalCode,
    country,
    province,
  };

  return address;
}

export async function generateCoordinatesFromPostalCode(
  postalCode: string,
  arcGISToken: string
) {
  try {
    const geocodeUrl = `${
      ""
      // process.env.REACT_APP_ARC_GIS_GEOCODE_ENDPOINT
    }${encodeURIComponent(postalCode)}&f=json&token=${arcGISToken}`;

    const response = await fetch(geocodeUrl);
    const data = await response.json();

    if (data.candidates && data.candidates.length > 0) {
      const candidate = data.candidates[0];

      // Extract address components from ArcGIS response
      const coordinates = {
        lat: candidate.location.y,
        lng: candidate.location.x,
      };

      return coordinates;
    }
  } catch (error) {
    console.error("Error generating coordinates from postal code:", error);
  }
}

export function generateInitialUserApplicationData(
  applicationType: string = "wildfire",
  lastStep: string = "Review",
  inReviewMode: boolean = true
) {
  const application: Application = new Application(generateApplicationData());

  return application;
}

export function generateStepData(): StepData {
  const personalDetails = generatePersonalDetails();

  const stepData = {
    Overview: {
      applicationTypeId: "853775A8-EB1C-463C-8EB0-8A7BD0AA10E9",
      applicationType: "wildfire",
      forcedToLeave: "Y",
      expectToEvacuate: "Y",
    },
    HowToApply: {},
    Verification: {},
    TermsOfUse: new TermsOfUse(),
    PersonalInformation: new PersonalInformation(personalDetails),
    EvacuationLocation: new EvacuationLocation(),
    TemporaryContactInfo: new TemporaryContactInfo(),
    FirstNationStatus: new FirstNationStatus(),
    FamilyMembers: new FamilyMembers(),
    PaymentInformation: {},
  };

  return stepData;
}

export function generateFamilyMember() {
  let relationship = "";
  const personalInformation = generatePersonalDetails();
  let maxAge = undefined;

  // randomize a relationship for the family member
  // Needs changing to getRefData
  const relationships = ["child", "spouse", "adult", "elderly"];
  const randomize = randomInt(4);
  relationship = relationships[randomize];

  if (relationship === "child") {
    maxAge = 16;
  }

  const familyMember: FamilyMember = {
    id: "",
    relationship: relationship,
    givenName: personalInformation.firstName,
    middleName: personalInformation.middleName,
    surname: personalInformation.lastName,
    dob: maxAge ? generateDOB(0, 16) : personalInformation.dob,
    gender: personalInformation.gender,
    isIndigenous: "N",
    band: "211",
    family: "10100",
    position: "10",
    FNIHB: "91919191",
    livesOnReserve: "Y",
    isGenerated: true,
  };

  return familyMember;
}

export function generateApplicationData(): ApplicationData {
  const applicationData: ApplicationData = {
    generation: 0,
    inReviewMode: false,
    lastStep: "Review",
    stepData: generateStepData(),
  };

  return applicationData;
}

export function generateDOB(minAge: number = 25, maxAge: number = 80) {
  const currentYear: number = new Date().getFullYear();
  const maxDay: number = 28;
  const generatedDay: number = randomInt(maxDay) + 1; // 1–28
  const maxMonth: number = 12;
  const generatedMonth: number = randomInt(maxMonth); // 0–11

  const generatedAgeOffset: number = randomInt(maxAge - minAge + 1);
  const generatedYear: number = currentYear - minAge - generatedAgeOffset;

  const birthDate: Date = new Date(generatedYear, generatedMonth, generatedDay);

  return birthDate;
}
